import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Loader2, User, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PublishOSPairing from "@/components/PublishOSPairing";
import BackupRestorePanel from "@/components/BackupRestorePanel";
import { tr } from "@/lib/i18n";

export default function AdminPage() {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">{tr("Not authorized")}</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">{tr("Only admins can access this page.")}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6">
          <div className="overline flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" />{tr("Admin")}</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">{tr("Properties & Users")}</h1>
        </div>
      </div>
      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        <PropertiesPanel />
        <UsersPanel />
        <BackupRestorePanel />
      </div>
    </div>
  );
}

function PropertiesPanel() {
  const qc = useQueryClient();
  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: api.adminListProperties,
  });
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  // PublishOS's own property list, for the pairing dropdown on each row.
  const { data: publishos } = useQuery({
    queryKey: ["admin", "publishos-properties"],
    queryFn: api.adminPublishosProperties,
    staleTime: 60_000,
  });

  const createProp = useMutation({
    mutationFn: () => api.adminCreateProperty({ name: newName, slug: newSlug }),
    onSuccess: () => {
      setNewName("");
      setNewSlug("");
      qc.invalidateQueries({ queryKey: ["admin", "properties"] });
      toast.success(tr("Property created"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Create failed")),
  });

  const deleteProp = useMutation({
    mutationFn: (id) => api.adminDeleteProperty(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "properties"] });
      toast.success(tr("Property deleted"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Delete failed")),
  });

  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-[var(--accent)]" />
        <div className="font-serif-display text-xl">{tr("Properties")}</div>
      </div>

      <div className="bg-white border border-[var(--border)]">
        {isLoading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline text-[var(--ink-muted)]" />
          </div>
        ) : (
          properties.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-property-${p.slug}`}
              className="flex items-start justify-between gap-3 px-5 py-3 border-b border-[var(--border)] last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{p.name}</div>
                <div className="text-xs text-[var(--ink-muted)] font-mono-display">
                  {tr("{slug} · {articles} articles · {users} users", { slug: p.slug, articles: p.counts?.articles ?? 0, users: p.counts?.users ?? 0 })}</div>
                <PublishOSPairing property={p} remote={publishos} />
              </div>
              <button
                data-testid={`admin-property-delete-${p.slug}`}
                onClick={() => {
                  if (window.confirm(tr("Delete property \"{name}\"?", { name: p.name }))) {
                    deleteProp.mutate(p.id);
                  }
                }}
                className="text-red-600 hover:bg-red-50 p-2"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 bg-white border border-[var(--border)] p-4">
        <div className="overline mb-2">{tr("Add new property")}</div>
        <div className="flex gap-2 flex-wrap">
          <input
            data-testid="admin-property-name-input"
            placeholder={tr("Property name (e.g. Gringo Gazette South)")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          />
          <input
            data-testid="admin-property-slug-input"
            placeholder={tr("slug")}
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value.replace(/[^a-z0-9-]/g, "-"))}
            className="w-32 px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          />
          <button
            data-testid="admin-property-add-btn"
            disabled={!newName.trim() || !newSlug.trim() || createProp.isPending}
            onClick={() => createProp.mutate()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate-900)] text-white text-sm disabled:opacity-60"
          >
            <Plus className="w-4 h-4" />{tr("Add")}</button>
        </div>
      </div>
    </div>
  );
}

function UsersPanel() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: api.adminListUsers,
  });
  const { data: properties = [] } = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: api.adminListProperties,
  });
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "editor", property_ids: [],
  });

  const createUser = useMutation({
    mutationFn: () => api.adminCreateUser(form),
    onSuccess: () => {
      setForm({ email: "", password: "", name: "", role: "editor", property_ids: [] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success(tr("User created"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Create failed")),
  });

  const patchUser = useMutation({
    mutationFn: ({ id, patch }) => api.adminUpdateUser(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success(tr("User updated"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Update failed")),
  });

  const deleteUser = useMutation({
    mutationFn: (id) => api.adminDeleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success(tr("User deleted"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Delete failed")),
  });

  const toggleProperty = (userId, propertyId, current) => {
    const next = current.includes(propertyId)
      ? current.filter((x) => x !== propertyId)
      : [...current, propertyId];
    patchUser.mutate({ id: userId, patch: { property_ids: next } });
  };

  const toggleFormProperty = (propertyId) => {
    setForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(propertyId)
        ? f.property_ids.filter((x) => x !== propertyId)
        : [...f.property_ids, propertyId],
    }));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <User className="w-4 h-4 text-[var(--accent)]" />
        <div className="font-serif-display text-xl">{tr("Users")}</div>
      </div>

      <div className="bg-white border border-[var(--border)]">
        {isLoading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline text-[var(--ink-muted)]" />
          </div>
        ) : (
          users.map((u) => (
            <div
              key={u.id}
              data-testid={`admin-user-${u.email}`}
              className="px-5 py-4 border-b border-[var(--border)] last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-medium text-sm">{u.email}</div>
                  <div className="text-xs text-[var(--ink-muted)] font-mono-display">
                    {u.name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={u.role}
                    onChange={(e) =>
                      patchUser.mutate({ id: u.id, patch: { role: e.target.value } })
                    }
                    className="text-xs border border-[var(--border)] px-2 py-1"
                  >
                    <option value="editor">{tr("editor")}</option>
                    <option value="admin">{tr("admin")}</option>
                  </select>
                  <button
                    onClick={() => {
                      if (window.confirm(tr("Delete user {email}?", { email: u.email }))) {
                        deleteUser.mutate(u.id);
                      }
                    }}
                    className="text-red-600 hover:bg-red-50 p-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {properties.map((p) => {
                  const active = u.property_ids?.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() =>
                        toggleProperty(u.id, p.id, u.property_ids || [])
                      }
                      className={
                        "px-2 py-0.5 text-[11px] uppercase tracking-wider font-mono-display border " +
                        (active
                          ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                          : "bg-white border-[var(--border)] text-[var(--ink-muted)] hover:border-[var(--ink)]")
                      }
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 bg-white border border-[var(--border)] p-4">
        <div className="overline mb-3">{tr("Add new user")}</div>
        <div className="space-y-2">
          <input
            placeholder="email@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          />
          <input
            placeholder={tr("Password")}
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          />
          <input
            placeholder={tr("Display name (optional)")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-[var(--border)] outline-none focus:border-[var(--ink)]"
          >
            <option value="editor">{tr("editor")}</option>
            <option value="admin">{tr("admin")}</option>
          </select>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display mb-1">{tr("Assign to properties")}</div>
            <div className="flex flex-wrap gap-2">
              {properties.map((p) => {
                const active = form.property_ids.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleFormProperty(p.id)}
                    className={
                      "px-2 py-0.5 text-[11px] uppercase tracking-wider font-mono-display border " +
                      (active
                        ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                        : "bg-white border-[var(--border)] text-[var(--ink-muted)] hover:border-[var(--ink)]")
                    }
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            data-testid="admin-user-add-btn"
            disabled={!form.email || !form.password || createUser.isPending}
            onClick={() => createUser.mutate()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate-900)] text-white text-sm disabled:opacity-60"
          >
            <Plus className="w-4 h-4" />{tr("Create user")}</button>
        </div>
      </div>
    </div>
  );
}
